import React from 'react'
import PropTypes from 'prop-types'
import { Toggler } from './Toggler'
import intl from 'react-intl-universal'
import { useEvidentlyFeatures } from '../../../evidentlyFeatureFlags'
import './styles.scss'

const LEGACY = 'legacy'
const OPEN_BETA = 'openBeta'
const NONE = ''

export const OpenBetaToggle = ({ companySettings, handleOpenBetaSettings }) => {
  const [legacyEnabled, setLegacyEnabled] = React.useState(false)
  const [openBetaEnabled, setOpenBetaEnabled] = React.useState(false)
  const [openBetaDefault, setOpenBetaDefault] = React.useState(false)
  const [isInlineCommentsEnabled, setIsInlineCommentsEnabled] = React.useState(
    true
  )

  const lastDisabledRef = React.useRef(NONE)
  const flags = useEvidentlyFeatures()
  const isV2Released = flags['ui-v2-release']

  const toggleOpenBeta = () => {
    setOpenBetaEnabled(!openBetaEnabled)
    lastDisabledRef.current = OPEN_BETA
  }

  const toggleOpenBetaDefault = () => {
    setOpenBetaDefault(!openBetaDefault)
  }

  const toggleLegacy = () => {
    setLegacyEnabled(!legacyEnabled)
    lastDisabledRef.current = LEGACY
  }

  React.useEffect(() => {
    if (companySettings) {
      setOpenBetaEnabled(Boolean(companySettings?.openBeta?.enabled))
      setOpenBetaDefault(Boolean(companySettings?.openBeta?.default))
      setLegacyEnabled(Boolean(companySettings?.openBeta?.legacyEnabled))
      setIsInlineCommentsEnabled(
        companySettings?.openBeta?.isInlineCommentsEnabled ?? true
      )
    }
    if (flags['ui-v2-release']) {
      setOpenBetaEnabled(true)
      setOpenBetaDefault(true)
    }
  }, [companySettings, flags])

  React.useEffect(() => {
    handleOpenBetaSettings({
      enabled: openBetaEnabled,
      byDefault: openBetaDefault,
      legacyEnabled,
      isInlineCommentsEnabled,
    })
  }, [
    openBetaEnabled,
    openBetaDefault,
    handleOpenBetaSettings,
    legacyEnabled,
    isInlineCommentsEnabled,
  ])

  React.useEffect(() => {
    // If both are false, enable the one that was most recently true
    if (!legacyEnabled && !openBetaEnabled) {
      // We can track which one was last disabled using a ref
      const lastChanged = lastDisabledRef.current

      if (lastChanged === LEGACY) {
        lastDisabledRef.current = OPEN_BETA
        setOpenBetaEnabled(true)
      } else if (lastChanged === OPEN_BETA) {
        setLegacyEnabled(true)
        lastDisabledRef.current = LEGACY
      }
    }
  }, [legacyEnabled, openBetaEnabled])

  return (
    <div className="card mb-2">
      <h2 className="card-header">{intl.get('settings.openBeta.header')}</h2>
      <div className="card-block">
        {!isV2Released && (
          <Toggler
            label={intl.get('settings.openBeta.legacy')}
            toggleFlag={legacyEnabled}
            toggleHandler={toggleLegacy}
            id="composer-settings--legacy-enabled"
          />
        )}
        {!isV2Released && (
          <Toggler
            label={intl.get('settings.openBeta.enabled')}
            toggleFlag={openBetaEnabled}
            toggleHandler={toggleOpenBeta}
            id="composer-settings--open-beta-enabled"
          />
        )}
        {openBetaEnabled && !isV2Released && (
          <Toggler
            label={intl.get('settings.openBeta.default')}
            toggleFlag={openBetaDefault}
            toggleHandler={toggleOpenBetaDefault}
            id="composer-settings--open-beta-default"
          />
        )}
        <Toggler
          label={intl.get('settings.openBeta.inlineCommentsEnabled')}
          toggleFlag={isInlineCommentsEnabled}
          toggleHandler={() => setIsInlineCommentsEnabled((prev) => !prev)}
          id="composer-settings--enable-inline-comments"
        />
      </div>
    </div>
  )
}

OpenBetaToggle.propTypes = {
  companySettings: PropTypes.object,
  handleOpenBetaSettings: PropTypes.func,
}
